/**
 * Route-level guard chặn URL-bypass khi user không có quyền View.
 *
 * Vì sao cần:
 * - Sidebar (`Layout.filterNavItems`) chỉ ẩn item khi `permissions[permKey] === 'NoGrant'`,
 *   nhưng user vẫn có thể paste URL vào address bar để vào page.
 * - `ProtectedRoute` chỉ check `isAuthenticated`, không check `canView`.
 *
 * Cách dùng: wrap `<Outlet/>` trong `Layout.tsx`. Tự động derive `permKey` từ
 * pathname qua `usePagePermission()` → check `canView`. Nếu false → redirect /home.
 *
 * Edge cases:
 * - Permissions chưa load (F5 trước khi `getRoles` về): fail-open render children
 *   để tránh false redirect lúc khởi tạo. `usePermission` cũng dùng pattern này
 *   (rank 'Edit' khi `!hasLoadedPerms`).
 * - Route không thuộc `ROUTE_PERM_MAP` (e.g. `/home`, `/profile`): `permKey`
 *   undefined → `usePermission` trả `level='Edit'` → canView=true → pass through.
 */
import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'

import { useAppSelector } from '../hooks/redux'
import { usePagePermission } from '../hooks/usePagePermission'

interface RequirePageViewProps {
  children: ReactNode
}

export default function RequirePageView({ children }: RequirePageViewProps) {
  const { pathname } = useLocation()
  const { canView } = usePagePermission()
  const permissions = useAppSelector((s) => s.auth.permissions)
  const permsLoaded = Object.keys(permissions).length > 0

  if (!permsLoaded) return <>{children}</>
  if (!canView) return <Navigate to="/home" replace state={{ blockedFrom: pathname }} />
  return <>{children}</>
}
